/* Time zones and formatting (ported from the prototype).
   Entries keep wall-clock times in the workspace zone ("2026-11-01T01:30:00") plus `so`, the UTC offset in minutes
   at the start. Wall-clock + offset is unambiguous even on daylight-saving nights, the end instant is always
   start + durationSeconds, and nothing depends on which browser/computer is used.
   Every helper takes an optional IANA zone (default: the workspace zone, Chicago). The database always stores Chicago;
   another zone is only for showing and typing times (each person's choice, Settings → Time zone; owner decision 2026-10-13). */
import {zoneTime, zoneShort, usZone} from './zonenames.ts';
import {zoneInfo} from './zonepick.ts';
export const WS_TZ = 'America/Chicago';
/** the day strip on the Timeline skins (Default, Timeline): 6:00 AM to 10:00 PM, minutes after midnight in the person's
    own zone. The night owl (surprises.ts) shows while a timer runs outside it. */
export const DAY_STRIP_FROM = 6 * 60, DAY_STRIP_TO = 22 * 60;
const dtfs = new Map<string, Intl.DateTimeFormat>();
function dtfFor(tz: string): Intl.DateTimeFormat {
  let f = dtfs.get(tz);
  if (!f) { f = new Intl.DateTimeFormat('en-CA', {timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'}); dtfs.set(tz, f); }
  return f;
}
function wparts(ms: number, tz = WS_TZ): Record<string, string> {
  const p: Record<string, string> = {};
  dtfFor(tz).formatToParts(new Date(ms)).forEach(x => { p[x.type] = x.value; });
  if (p.hour === '24') p.hour = '00';
  return p;
}
/** the zone's UTC offset in minutes at an instant (Chicago: -300 CDT, -360 CST; Manila: +480) */
export function offsetAtMs(ms: number, tz = WS_TZ): number {
  const p = wparts(ms, tz);
  return Math.round((Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - Math.floor(ms / 1000) * 1000) / 60000);
}
/** the wall-clock time "YYYY-MM-DDTHH:MM:SS" an instant shows in a zone */
export function wallOf(ms: number, tz = WS_TZ): string { const p = wparts(ms, tz); return p.year + '-' + p.month + '-' + p.day + 'T' + p.hour + ':' + p.minute + ':' + p.second; }
export function wallAsUtc(w: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/.exec(w || '');
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]) : NaN;
}
/** every real instant that shows this wall-clock time in a zone: none (clocks skipped it), one, or two (clocks repeated it) */
export function wallToMsList(w: string, tz = WS_TZ): number[] {
  const u = wallAsUtc(w); if (isNaN(u)) return [];
  const out: number[] = [];
  new Set([offsetAtMs(u - 86400000, tz), offsetAtMs(u, tz), offsetAtMs(u + 86400000, tz)]).forEach(o => { const ms = u - o * 60000; if (offsetAtMs(ms, tz) === o) out.push(ms); });
  return out.sort((a, b) => a - b);
}
/** a wall-clock time that does not exist in the zone (inside the hour the clocks skip in spring) */
export const isGapWall = (w: string, tz = WS_TZ) => !isNaN(wallAsUtc(w)) && !wallToMsList(w, tz).length;
/** the start instant for a wall-clock time in a zone; keeps a known offset, otherwise the first occurrence.
    A time that never existed (clocks jumped) gives the moment just after the jump: callers that take typed times check
    isGapWall first and refuse it with gapMessage (never shifted silently). */
export function startMsFor(w: string, preferOffset?: number | null, tz = WS_TZ): number {
  const c = wallToMsList(w, tz);
  if (c.length) {
    if (preferOffset != null) { const hit = c.find(ms => offsetAtMs(ms, tz) === preferOffset); if (hit !== undefined) return hit; }
    return c[0];
  }
  const u = wallAsUtc(w); return isNaN(u) ? NaN : u - offsetAtMs(u - 86400000, tz) * 60000; // time that never existed (clocks jumped): just after the jump
}
/** the zone's short name at an instant, e.g. CDT / CST / EDT, or GMT+8 where there is no common abbreviation */
export function zoneAbbr(ms: number, tz = WS_TZ): string {
  try { return new Intl.DateTimeFormat('en-US', {timeZone: tz, timeZoneName: 'short'}).formatToParts(new Date(ms)).find(p => p.type === 'timeZoneName')?.value || ''; } catch { return ''; }
}
/** which side of a repeated hour an instant is on, in words: "Central daylight" / "Central standard" (US zones), else the
    zone's abbreviation ("BST" / "GMT") or offset ("UTC+1") */
export function dstName(ms: number, tz = WS_TZ): string {
  const us = usZone(tz);
  if (us) { const y = new Date(ms).getUTCFullYear(), std = Math.min(offsetAtMs(Date.UTC(y, 0, 1), tz), offsetAtMs(Date.UTC(y, 6, 1), tz));
    return zoneShort(tz) + (offsetAtMs(ms, tz) > std ? ' daylight' : ' standard'); }
  const i = zoneInfo(tz, ms); return i.abbr || i.utc;
}
/** a wall-clock time the clocks show twice (Chicago, Nov 1 2026: 1:00–1:59 AM): both readings, first one first, each with its
    offset and its words, e.g. [{ms, so: -300, label: 'the first 1:30 AM (Central daylight)'}, {…, so: -360, label: 'the
    second 1:30 AM (Central standard)'}]; [] for a time that happens once (or never). Typed-time windows show this as a choice,
    so the second reading can be typed too and neither is picked silently (owner check 2026-10-30, before the clock change). */
export function repeatedReadings(w: string, tz = WS_TZ): {ms: number; so: number; label: string}[] {
  const c = wallToMsList(w, tz); if (c.length < 2) return [];
  const h = +w.slice(11, 13), t = (h % 12 || 12) + ':' + w.slice(14, 16) + (h < 12 ? ' AM' : ' PM');
  return c.map((ms, i) => ({ms, so: offsetAtMs(ms, tz), label: `the ${i ? 'second' : 'first'} ${t} (${dstName(ms, tz)})`}));
}
/** after a clock time shown in a list: which reading it is when the clocks show that time twice in the zone (Chicago, Nov 1
    2026, 1:00-1:59 AM: " CDT" for the first, " CST" for the second); '' for every other time (spring-forward day, Manila,
    any normal time). Display only (owner check 2026-10-30): a first-1:30 and a second-1:25 entry must not look as if they overlap. */
export function repeatMark(ms: number, tz = WS_TZ): string {
  if (!isFinite(ms) || wallToMsList(wallOf(ms, tz), tz).length < 2) return '';
  const i = zoneInfo(tz, ms); return ' ' + (i.abbr || zoneAbbr(ms, tz) || i.utc);
}
/** for a wall-clock time the clocks skipped: the instant of the jump and the wall clock just before and after it
    (Chicago, Mar 8 2026: 2:00 AM jumps to 3:00 AM) */
export function gapJump(w: string, tz = WS_TZ): {ms: number; before: string; after: string} {
  const u = wallAsUtc(w), o1 = offsetAtMs(u - 86400000, tz);
  let lo = u - o1 * 60000 - 86400000, hi = u - o1 * 60000;                 // before the jump / after it: find the switch instant
  while (hi - lo > 1000) { const mid = Math.floor((lo + hi) / 2); if (offsetAtMs(mid, tz) === o1) lo = mid; else hi = mid; }
  const at = Math.floor(hi / 60000) * 60000;
  return {ms: at, before: wallOf(at - 60000, tz), after: wallOf(at, tz)};
}
/** the clear message for a typed time inside the skipped spring hour, e.g. "2:30 AM does not exist on Sun, Mar 8 in Chicago:
    the clocks jump from 2:00 AM to 3:00 AM. Pick a time before or after." */
export function gapMessage(w: string, tz = WS_TZ): string {
  const u = wallAsUtc(w), j = gapJump(w, tz), skippedFrom = wallAsUtc(j.before) + 60000;
  const h12 = (ms: number) => { const x = new Date(ms), h = x.getUTCHours(); return (h % 12 || 12) + ':' + String(x.getUTCMinutes()).padStart(2, '0') + (h < 12 ? ' AM' : ' PM'); };
  const day = new Date(w.slice(0, 10) + 'T12:00:00Z').toLocaleDateString('en-US', {timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric'});
  return `${h12(u)} does not exist on ${day} in ${zoneTime(tz)}: the clocks jump from ${h12(skippedFrom)} to ${h12(wallAsUtc(j.after))}. Pick a time before or after.`;
}
/** THE way an entry's stored times are made (owner decision 2026-10-13: every save starts from the exact instant): Chicago
    wall clock at the start, Chicago's real UTC offset at that instant, the Chicago day it starts on, and the end as
    start + duration. The database refuses any other offset (migration 014), so nothing built here can trip it. */
export function storedTimes(startMs: number, durationSeconds: number): {date: string; startedAt: string; endedAt: string; so: number} {
  const startedAt = wallOf(startMs, WS_TZ);
  return {date: startedAt.slice(0, 10), startedAt, endedAt: wallOf(startMs + durationSeconds * 1000, WS_TZ), so: offsetAtMs(startMs, WS_TZ)};
}
/** the real instant an entry starts, from its stored wall-clock + offset */
export function entryStartMs(startedAt: string, so: number): number { return wallAsUtc(startedAt) - so * 60000; }
/** the real instant an entry ends: its start (wall-clock + offset) + its duration (never read from the end clock string) */
export const entryEndMs = (e: {startedAt: string; so: number; durationSeconds: number}) => entryStartMs(e.startedAt, e.so) + e.durationSeconds * 1000;
/** lists newest first, by the real start moment (stored wall clock + offset), never by the clock strings: on Nov 1 2026 the
    second 1:25 AM (CST) is later than the first 1:30 AM (CDT) and is listed above it (owner check 2026-10-30). Equal starts
    keep their order (0). A row without a readable start/offset falls back to the clock strings (none: last), as before. */
export function newestStartFirst(a: {startedAt: string; so: number}, b: {startedAt: string; so: number}): number {
  const x = entryStartMs(a.startedAt, a.so), y = entryStartMs(b.startedAt, b.so);
  return isFinite(x) && isFinite(y) ? y - x : (b.startedAt || '').localeCompare(a.startedAt || '');
}
/** Magic Time Sync: this person's entry that ended last, at or before limitMs (excludeId: the entry being edited) */
export function lastEndBefore<E extends {id: string; memberId: string; startedAt: string; so: number; durationSeconds: number}>(entries: E[], memberId: string, limitMs: number, excludeId?: string): {ms: number; entry: E} | null {
  let best: {ms: number; entry: E} | null = null;
  for (const e of entries) {
    if (e.memberId !== memberId || e.id === excludeId) continue;
    const ms = entryEndMs(e); if (isNaN(ms) || ms > limitMs) continue;
    if (!best || ms > best.ms) best = {ms, entry: e};
  }
  return best;
}
/** Magic Time Sync starts one second after the previous entry ended, so the two never share a second (owner request 2026-10-13) */
export const SYNC_GAP_MS = 1000;
/** the start Magic Time Sync gives: one second after this person's latest end at or before limitMs (see lastEndBefore) */
export function syncStartAfter<E extends {id: string; memberId: string; startedAt: string; so: number; durationSeconds: number}>(entries: E[], memberId: string, limitMs: number, excludeId?: string): {ms: number; endMs: number; entry: E} | null {
  const last = lastEndBefore(entries, memberId, limitMs, excludeId);
  return last ? {ms: last.ms + SYNC_GAP_MS, endMs: last.ms, entry: last.entry} : null;
}
export function todayStr(tz = WS_TZ): string { return wallOf(Date.now(), tz).slice(0, 10); } // today's date in a zone (default: the workspace zone)

/** the first instant of a day in a zone (its midnight; where the clocks skip midnight, the moment just after the jump) */
export const dayStartMs = (date: string, tz = WS_TZ) => startMsFor(date + 'T00:00:00', null, tz);
/** how many whole seconds of a running timer (started at startMs, now nowMs) fall on a day in a zone, midnight to midnight.
    Never negative; a timer over 24 hours gives each full day its whole length (23 or 25 hours on a daylight-saving night). */
export function secondsOnDay(startMs: number, nowMs: number, date: string, tz = WS_TZ): number {
  if (!(nowMs > startMs)) return 0;
  const from = Math.max(startMs, dayStartMs(date, tz)), to = Math.min(nowMs, dayStartMs(addDays(date, 1), tz));
  return to > from ? Math.floor((to - from) / 1000) : 0;
}
/** each day (in a zone) a running timer touches, oldest first, with its seconds on that day (days from first on only) */
export function timerDays(startMs: number, nowMs: number, tz = WS_TZ, first = ''): {date: string; sec: number}[] {
  if (!(nowMs > startMs)) return [];
  const out: {date: string; sec: number}[] = [], last = wallOf(nowMs, tz).slice(0, 10);
  let d = wallOf(startMs, tz).slice(0, 10);
  if (first && d < first) d = first;
  for (let i = 0; d <= last && i < 400; i++, d = addDays(d, 1)) { const sec = secondsOnDay(startMs, nowMs, d, tz); if (sec > 0) out.push({date: d, sec}); }
  return out;
}

export function isoDate(d: Date): string { const p = (n: number) => String(n).padStart(2, '0'); return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()); }
export function addDays(iso: string, n: number): string { const [y, m, d] = iso.split('-').map(Number); return isoDate(new Date(y, m - 1, d + n)); }
export function daysBetween(a: string, b: string): number {
  const [y1, m1, d1] = a.split('-').map(Number), [y2, m2, d2] = b.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
}
export function weekStart(iso: string): string { const [y, m, d] = iso.split('-').map(Number); const dt = new Date(y, m - 1, d); const dow = (dt.getDay() + 6) % 7; dt.setDate(dt.getDate() - dow); return isoDate(dt); } // Monday
export function monthEnd(ym: string): string { const [y, m] = ym.split('-').map(Number); return isoDate(new Date(y, m, 0)); }
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export function wdOf(iso: string): string { const [y, m, d] = iso.split('-').map(Number); return WD[new Date(y, m - 1, d).getDay()]; }

/** "$1,234.50"; below zero "−$5.14" (minus sign before the dollar sign), never "$-5.14" or "−$0.00" */
export function fmtMoney(n: number): string {
  if (!isFinite(n)) n = 0;
  const c = Math.round(n * 100);
  return (c < 0 ? '\u2212' : '') + '$' + (Math.abs(c) / 100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
}
export function fmtHM(totalSeconds: number): string {
  totalSeconds = Math.max(0, Math.round(totalSeconds || 0));
  const h = Math.floor(totalSeconds / 3600), m = Math.floor((totalSeconds % 3600) / 60), s = totalSeconds % 60;
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
}
/** hours with 2 decimals, rounded exactly (half up) like the database (2021 total is exactly 1,574.715 h -> 1574.72) */
export const hh = (sec: number) => (Math.round(sec / 36) / 100).toFixed(2);
/** hours and minutes, rounded to the minute: 1907 s -> "0:32" (shown next to decimal hours everywhere) */
export function hmm(sec: number): string { const m = Math.round(Math.max(0, sec || 0) / 60); return Math.floor(m / 60) + ':' + String(m % 60).padStart(2, '0'); }
/** both ways: "0.53h (0:32)" */
export function fmtHours(totalSeconds: number): string { return hh(totalSeconds) + 'h (' + hmm(totalSeconds) + ')'; }
export function fmtN(n: number): string { return Number(n).toLocaleString('en-US'); }

export function parseDurationStr(str: string | null | undefined): number | null {
  if (str == null) return null;
  str = String(str).trim();
  if (!str) return null;
  let m = str.match(/^(\d+):(\d{1,2})\s*min$/i);          // Toggl: "90:46 min" = 90 min 46 sec (minutes may exceed 60)
  if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  m = str.match(/^(\d+):(\d{2}):(\d{2})$/);               // H:MM:SS
  if (m) return parseInt(m[1], 10) * 3600 + parseInt(m[2], 10) * 60 + parseInt(m[3], 10);
  m = str.match(/^(\d+):(\d{2})$/);                       // H:MM
  if (m) return parseInt(m[1], 10) * 3600 + parseInt(m[2], 10) * 60;
  if (/^\d+$/.test(str)) return parseInt(str, 10);        // plain seconds
  return null;
}
/** what a person types in the Duration box: 1:30, 1:30:15, or with units: 2m, 2 m, 90 min, 1.5h, 1h 30m, 1 h 30 m, 45s.
    A plain number ("45") is refused (minutes? hours?): null, like anything else it cannot read. (The importer keeps
    parseDurationStr, where a plain number is seconds.) */
export function parseDurationInput(str: string | null | undefined): number | null {
  const s = String(str ?? '').trim().toLowerCase();
  if (!s) return null;
  let m = s.match(/^(\d+):(\d{2}):(\d{2})$/);
  if (m) return +m[1] * 3600 + +m[2] * 60 + +m[3];
  m = s.match(/^(\d+):(\d{2})$/);
  if (m) return +m[1] * 3600 + +m[2] * 60;
  const part = /(\d+(?:\.\d+)?|\.\d+)\s*(hours?|hrs?|h|minutes?|mins?|m|seconds?|secs?|s)(?![a-z])/y;
  let i = 0, total = 0, n = 0;
  const seen = new Set<string>();
  while (i < s.length) {
    while (s[i] === ' ' || s[i] === ',') i++;
    if (i >= s.length) break;
    part.lastIndex = i;
    const p = part.exec(s); if (!p) return null;
    const unit = p[2][0];                      // h / m / s
    if (seen.has(unit)) return null; seen.add(unit);
    total += parseFloat(p[1]) * (unit === 'h' ? 3600 : unit === 'm' ? 60 : 1); n++; i = part.lastIndex;
  }
  return n ? Math.round(total) : null;
}
export function parseTimeStr(str: string | null | undefined): string | null {
  if (!str) return null;
  const m = String(str).trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10); const min = parseInt(m[2], 10), sec = m[3] ? parseInt(m[3], 10) : 0, ap = m[4];
  if (ap) { if (/pm/i.test(ap) && h !== 12) h += 12; if (/am/i.test(ap) && h === 12) h = 0; }
  if (h > 23 || min > 59 || sec > 59) return null;
  return String(h).padStart(2, '0') + ':' + String(min).padStart(2, '0') + ':' + String(sec).padStart(2, '0');
}
